import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { Button } from '../ui/button.js';
import { Minimize2, Maximize2, Trash2 } from 'lucide-react';
import '@xterm/xterm/css/xterm.css';

interface TerminalPaneProps {
  height?: number;
  onHeightChange?: (height: number) => void;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
}

const TerminalPane: React.FC<TerminalPaneProps> = ({ 
  height = 200,
  onHeightChange,
  isMinimized: externalIsMinimized,
  onToggleMinimize: externalOnToggleMinimize
}) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminal = useRef<Terminal | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [internalIsMinimized, setInternalIsMinimized] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('beehive-terminal-minimized');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });

  const isMinimized = externalIsMinimized !== undefined ? externalIsMinimized : internalIsMinimized;
  const [logs] = useState<string[]>([
    '[INFO] Terminal initialized',
    '[INFO] Ready for commands',
    `[INFO] Session ID: ${typeof window !== 'undefined' ? localStorage.getItem('beehive-session-id') || 'unknown' : 'unknown'}`
  ]);

  useEffect(() => {
    if (!terminalRef.current || isMinimized) return;

    terminal.current = new Terminal({
      theme: {
        background: '#0f172a',
        foreground: '#e2e8f0',
        cursor: '#06b6d4',
      },
      fontSize: 12,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      cursorBlink: true,
      rows: Math.floor((height - 40) / 17),
      cols: 80
    });

    terminal.current.open(terminalRef.current);
    
    logs.forEach(log => {
      terminal.current?.writeln(log);
    });

    setIsConnected(true);

    terminal.current.onData((data) => {
      if (data === '\r') {
        terminal.current?.write('\r\n$ ');
      } else {
        terminal.current?.write(data);
      }
    });

    return () => {
      terminal.current?.dispose();
    };
  }, [height, isMinimized, logs]);

  const toggleMinimize = externalOnToggleMinimize || (() => {
    const newMinimized = !internalIsMinimized;
    setInternalIsMinimized(newMinimized);
    if (typeof window !== 'undefined') {
      localStorage.setItem('beehive-terminal-minimized', JSON.stringify(newMinimized));
    }
    if (onHeightChange) {
      onHeightChange(newMinimized ? 32 : height);
    }
  });

  return (
    <div 
      className="bg-slate-900 border-t border-muted flex flex-col flex-shrink-0"
      style={{ height: isMinimized ? 32 : 60, marginBottom: '0px' }}
    >
      <div className="flex items-center justify-between px-3 py-1 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className="text-xs text-slate-300">Terminal</span>
        </div>
        <div className="flex items-center space-x-1">
          <Button size="sm" variant="ghost" onClick={() => terminal.current?.clear()} className="violett-button text-drop-shadow h-6 w-6 p-0">
            <Trash2 className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleMinimize} className="violett-button text-drop-shadow h-6 w-6 p-0">
            {isMinimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
          </Button>
        </div>
      </div>
      
      {!isMinimized && (
        <div 
          ref={terminalRef} 
          className="flex-1 w-full p-2 min-h-0"
          style={{ 
            backgroundColor: '#0f172a'
          }}
        />
      )}
    </div>
  );
};

export default TerminalPane;
